/**
 * Mechanism to check if the target element is visible, i.e. placed inside the viewport so the user can see it.
 *
 * It relies on IntersectionObserver API.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver
 *
 * @example
 * ```js
 * const targetElement = document.querySelector('#some-element');
 *
 * // Callback that's called every time the element enters the viewport.
 * // Value `intersectionRatio` is 1 if the element is fully visible, and less in other cases.
 * function visibilityCallback(intersectionRatio) {
 *     console.log('Target element is visible with the ratio of', intersectionRatio);
 * }
 *
 * const elementVisibility = new ElementVisibility(targetElement, visibilityCallback);
 *
 * // Start observing
 * elementVisibility.observe();
 *
 * // Cleanup once this mechanism is not needed
 * elementVisibility.remove();
 * ```
 */
export default class ElementVisibility {
    constructor(element, callback) {
        this.callback = callback;
        this.element = element;
    }

    observe() {
        const options = {
            thresholds: [0, 0.25, 0.5, 0.75, 1],
        };

        const listener = (entries) => {
            entries.forEach((entry) => {
                if (entry.intersectionRatio > 0) {
                    this.callback(entry.intersectionRatio);
                }
            });
        };

        this.observer = new IntersectionObserver(listener, options);
        this.observer.observe(this.element);
    }

    remove() {
        this.observer.unobserve(this.element);
    }
}
